import { useState } from 'react';
import type { LabelDTO } from '@jynexus/shared';
import { useAgents, useUsers } from '../lib/hooks.ts';
import { LabelChip } from './badges.tsx';
import { Select } from './ui.tsx';

/** Escolhe etiquetas existentes (não cria aqui: criação fica na tela de etiquetas). */
export function LabelPicker({ labels, value, onChange }: { labels: LabelDTO[]; value: string[]; onChange: (v: string[]) => void }) {
  const [q, setQ] = useState('');
  const available = labels.filter((l) => !value.includes(l.name) && l.name.includes(q.toLowerCase())).slice(0, 12);
  const byName = new Map(labels.map((l) => [l.name, l]));
  return (
    <div className="rounded-md border border-slate-300 bg-white p-1.5">
      <div className="flex flex-wrap gap-1">
        {value.map((n) => (
          <LabelChip key={n} name={n} color={byName.get(n)?.color ?? '#64748b'} onRemove={() => onChange(value.filter((x) => x !== n))} />
        ))}
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              const exact = available.find((l) => l.name === q.toLowerCase()) ?? available[0];
              if (exact) (onChange([...value, exact.name]), setQ(''));
            }
          }}
          placeholder={value.length ? '' : 'Filtrar etiquetas…'}
          aria-label="Filtrar etiquetas"
          className="min-w-24 flex-1 px-1 text-sm outline-none"
        />
      </div>
      {(q || !value.length) && available.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1 border-t border-slate-100 pt-1.5">
          {available.map((l) => (
            <button key={l.id} type="button" onClick={() => (onChange([...value, l.name]), setQ(''))} className="rounded-full hover:ring-2 hover:ring-navy-100">
              <LabelChip name={l.name} color={l.color} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Valor: "" | "user:<id>" | "agent:<id>" */
export function AssigneeSelect({ value, onChange, allowNone = true, includeFilters = false, ...rest }: { value: string; onChange: (v: string) => void; allowNone?: boolean; includeFilters?: boolean; 'aria-label'?: string; className?: string }) {
  const users = useUsers();
  const agents = useAgents();
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} {...rest}>
      {includeFilters ? <option value="">Qualquer responsável</option> : allowNone && <option value="">Sem responsável</option>}
      {includeFilters && <option value="none">Sem responsável</option>}
      {includeFilters && <option value="me">Eu</option>}
      <optgroup label="Pessoas">
        {users.data?.filter((u) => u.active).map((u) => (
          <option key={u.id} value={`user:${u.id}`}>
            {u.name}
          </option>
        ))}
      </optgroup>
      <optgroup label="Agentes">
        {agents.data?.filter((a) => a.active).map((a) => (
          <option key={a.id} value={`agent:${a.id}`}>
            {a.name}
          </option>
        ))}
      </optgroup>
    </Select>
  );
}

export const assigneeRef = (v: string) => {
  if (!v) return null;
  const [kind, id] = v.split(':');
  return { kind: kind as 'user' | 'agent', id };
};
